import React, { useCallback, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
// --- Data ---
const faqs = [{
  question: '¿Cómo recibo el MegaPack después de comprar?',
  answer: 'Al completar el pago recibís un email con el link de descarga en menos de 5 minutos. Son archivos PDF que podés abrir desde cualquier dispositivo: celular, tablet o computadora. No necesitás ningún programa especial ni suscripción adicional.'
}, {
  question: '¿Sirve para profes particulares o solo para academias?',
  answer: 'Sirve para los dos. El 70% de los profesores que usan el MegaPack son independientes con clases particulares. El 30% trabaja en academias. El material está diseñado para funcionar en cualquier contexto porque resuelve la pedagogía, no la infraestructura.'
}, {
  question: '¿Es editable? ¿Puedo modificarlo?',
  answer: 'Los archivos son PDF. Podés imprimirlos, usarlos en pantalla y completar las planillas digitalmente con cualquier lector de PDF básico. Todas las planillas, fichas y checklists tienen espacios para completar a mano o digitalmente.'
}, {
  question: '¿Los 6 bonos están en las dos ofertas?',
  answer: 'Sí. Los 6 bonos están incluidos en las dos ofertas sin costo adicional. La diferencia es que el combo MegaPack + Ecosistema del Alumno Pro incluye el kit completo de materiales para entregar a tus alumnos: cuaderno personal, diccionario de figuras, guía de primera milonga, bitácora de progreso, 50 infografías para WhatsApp y licencia de distribución.'
}, {
  question: '¿Sirve si soy profe nuevo con poca experiencia?',
  answer: 'Especialmente. El MegaPack fue construido para que un bailarín con conocimiento de tango pero sin formación pedagógica tenga un sistema completo desde el primer día. Las 18 secuencias didácticas, los 80+ guiones de corrección y el Framework 10-10-20-15-5 te permiten dar una clase profesional desde la primera vez.'
}, {
  question: '¿Sirve para alumnos de todas las edades?',
  answer: 'Sí. El material tiene indicaciones específicas para adultos jóvenes, adultos mayores y parejas. El Tomo 4 incluye los 4 perfiles psicológicos del alumno (técnico, social, emocional y desafiante) con estrategias específicas para cada uno.'
}, {
  question: '¿Qué pasa si tengo alumnos de distintos niveles?',
  answer: 'Está completamente resuelto. El Tomo 1 tiene el Sistema de Zonas con instrucciones diferenciadas para grupos mixtos. El Bono 3 incluye diagramas y protocolos específicos para gestionar grupos donde los niveles son diferentes sin que nadie se aburra ni se pierda.'
}, {
  question: '¿Qué diferencia tiene con otros materiales de tango?',
  answer: 'La mayoría del material de tango está diseñado para el bailarín, no para el docente. El MegaPack está construido desde la pedagogía: secuencias didácticas, criterios de evaluación, 80+ guiones de corrección, sistema de retención y plan de negocio. No existe otro material así en el mercado.'
}, {
  question: '¿Por qué elegir el combo con Ecosistema del Alumno Pro?',
  answer: 'Si solo querés resolver las clases, el MegaPack estándar es suficiente. Si además querés entregar material profesional a tus alumnos y construir una comunidad que retiene, el combo con el Ecosistema del Alumno Pro es la mejor inversión. El Cuaderno del Milonguero, la Guía de Primera Milonga y las 50 infografías solos justifican la diferencia.'
}, {
  question: '¿Tiene garantía real?',
  answer: 'Real y sin letra chica. Si en los primeros 7 días no es lo que esperabas, escribinos a soporteprofepack@gmail.com y te devolvemos el 100%. Sin preguntas, sin formularios. Menos del 1% de devoluciones.'
}];
// --- Animation variants ---
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.05
    }
  }
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3
    }
  }
};
const expandCollapse = {
  initial: {
    height: 0,
    opacity: 0
  },
  animate: {
    height: 'auto',
    opacity: 1
  },
  exit: {
    height: 0,
    opacity: 0
  },
  transition: {
    duration: 0.25,
    ease: 'easeInOut'
  }
};
// --- FAQ Item ---
function FAQItem({
  faq,
  index,
  isOpen,
  onToggle
}: {
  faq: {
    question: string;
    answer: string;
  };
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <motion.div variants={fadeUp} className={`rounded-xl overflow-hidden transition-all duration-300 border-l-[3px] ${isOpen ? 'border-[#C8962E]/40 border-l-[#C8962E] bg-white/[0.03]' : 'border-white/[0.05] border-l-transparent bg-white/[0.01] hover:bg-white/[0.02] hover:border-white/10'}`} data-id="element-65">
      <button className="w-full text-left px-5 py-4 sm:py-5 flex items-center justify-between gap-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8962E]/50 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] rounded-xl" onClick={onToggle} aria-expanded={isOpen} data-id="element-66">
        <span className={`font-bold text-sm sm:text-base transition-colors duration-300 ${isOpen ? 'text-[#C8962E]' : 'text-white'}`} data-id="element-67">
          {index + 1}. {faq.question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'text-[#C8962E] rotate-180' : 'text-gray-500'}`} data-id="element-68" />
      </button>
      <AnimatePresence data-id="element-69">
        {isOpen && <motion.div initial={expandCollapse.initial} animate={expandCollapse.animate} exit={expandCollapse.exit} transition={expandCollapse.transition} data-id="element-70">
            <div className="px-5 pb-5 sm:pb-6" data-id="element-71">
              <div className="h-px bg-gradient-to-r from-[#C8962E]/20 to-transparent mb-4" data-id="element-72" />
              <p className="text-gray-300 text-sm leading-relaxed" data-id="element-73">
                {faq.answer}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
// --- Section ---
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const toggle = useCallback((i: number) => {
    setOpenIndex(prev => prev === i ? null : i);
  }, []);
  return <section className="w-full bg-[#0a0a0a] py-16 sm:py-24 px-4" data-id="element-74">
      <div className="max-w-2xl mx-auto" data-id="element-75">
        <motion.div className="text-center mb-10 sm:mb-12" initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-40px'
      }} transition={{
        duration: 0.5
      }} data-id="element-76">
          <p className="text-[#C8962E]/60 text-xs font-bold tracking-[0.25em] uppercase mb-3" data-id="element-77">
            DUDAS RESUELTAS
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 leading-tight" data-id="element-78">
            Preguntas <span className="text-[#C8962E]" data-id="element-79">Frecuentes</span>
          </h2>
        </motion.div>

        <motion.div className="space-y-3" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} variants={staggerContainer} data-id="element-80">
          {faqs.map((faq, i) => <FAQItem key={i} faq={faq} index={i} isOpen={openIndex === i} onToggle={() => toggle(i)} data-id="element-81" />)}
        </motion.div>
      </div>
    </section>;
}